import './DataAccessSearch.scss';

import { AccessAuditQueryParams } from './AccessAudit';
import { AccessExplainerQueryParams } from './AccessExplainer';
import { capitalize } from '@sonar-software/sonar-ts-lib';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { createAsyncSelectors } from '@lib/selectors';
import { DataAccessCompareOption } from './DataAccessCompareButton';
import type { DefaultOptionType } from 'antd/es/select';
import { getDataAccessRoute } from '@lib/routes';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { RecentSearchResults } from './RecentSearchResults';
import { SearchOutlined } from '@ant-design/icons';
import { ShowMoreTypeLabel } from './ShowMoreTypeLabel';
import { useNavigate } from 'react-router-dom';

import { AccessType, coreActions, SEARCH_LIMIT, TYPE_SEARCH_LIMIT } from '@sonar-software/sonar-core';
import { Button, Select, Space } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAction, useAsyncState } from '@hooks';

export const dataAccessSearchDataSelectors =
    createAsyncSelectors((state) => state.core.dataSecurity.dataAccess.search.get);

export interface DataAccessSearchProps {
    allowClear?: boolean;
    defaultValue?: string;
    onSelect?: (label: string, type: AccessType, value: string) => void;
    placeholder?: string;
	showRecent?: boolean;
	showCompare?: boolean;
    specificType?: AccessType;
}

type SearchOption = { label: string; type: AccessType; value: string; };
const MIN_SEARCH_QUERY_LENGTH = 3;

export const DataAccessSearch: React.FC<DataAccessSearchProps> = ({
	allowClear = true,
	defaultValue,
	onSelect,
	placeholder,
	showRecent,
	showCompare,
	specificType
}) => {
	const [ query, setQuery ] = useState('');
	const [ currentType, setCurrentType ] = useState<AccessType | undefined>(specificType);
	const navigate = useNavigate();

	const { data, isRequested, isReceived } = useAsyncState(dataAccessSearchDataSelectors);

	const getSearch = useAction(coreActions.requestGetCoreDataSecurityDataAccessSearch);
	const resetSearch = useAction(coreActions.resetGetCoreDataSecurityDataAccessSearch);

	useEffect(() => {
		if (query?.length >= MIN_SEARCH_QUERY_LENGTH) {
			getSearch({
				queryString: query,
				searchLimit: currentType ? TYPE_SEARCH_LIMIT : SEARCH_LIMIT,
				target: currentType ? [ currentType ] : undefined
			});
		}
	}, [ currentType, getSearch, query ]);

	const handleOnSearch = useCallback((value: string) => {
		const cleanValue = value?.trim();

		setQuery(cleanValue);

		if (!cleanValue?.length || (!currentType && data !== null)) {
			setCurrentType(specificType);
			resetSearch();
		}
	}, [ currentType, data, resetSearch, specificType ]);

	const options = useMemo(() => {
		if (!data) {
			return [];
		}

		return Object.keys(data).map((type: string) => {
			const typeResults = data[ type ] ?? [];
			const typeCorrected = capitalize(type) as AccessType;

			const label = <ShowMoreTypeLabel
				allowShowMore={ currentType !== typeCorrected }
				onClick={ setCurrentType }
				type={ typeCorrected } />;

			return {
				label: label,
				options: typeResults.map((entry):SearchOption => ({
					label: entry.display,
					type: typeCorrected,
					value: entry.id
				}))
			};
		});
	}, [ currentType, data ]);

	const hasNoResults = data && isReceived &&
  Object.values(data).every((typeResults) => typeResults.length === 0);

	const getNotFoundContent = (() => {
		if (isRequested) {
			return <LoadingSpinner
				divClassName='search-spinner'
				spinning />;
		}

		if (hasNoResults && query.length >= MIN_SEARCH_QUERY_LENGTH) {
			return COPY_SPECIFIC.NO_RESULTS_FOUND;
		}

		return null;
	})();

	const handleSelect = useCallback((option: SearchOption | unknown) => {
		const { label, type, value } = option as SearchOption;

		if (onSelect) {
			onSelect(label, type, value);
			resetSearch();
		} else if (type === AccessType.Object) {
			const queryObject: AccessAuditQueryParams = {
				objectName: label
			};

			navigate(
				getDataAccessRoute(queryObject)
			);
		} else {
			const queryObject: AccessExplainerQueryParams = {
				accessName: label,
				type
			};

			navigate(
				getDataAccessRoute({
					id: value,
					...queryObject
				})
			);
		}

	}, [ navigate, onSelect, resetSearch ]);

	// The "Compare" button should not appear on the Access Explainer or Access Audit pages,
	// as those pages deal with Salesforce objects, not access objects.
	const renderOption = useCallback((option: DefaultOptionType) => {
		const searchOption = option.data as SearchOption;
		const shouldSkipComparison = !showCompare || searchOption.type === AccessType.Object;

		if (shouldSkipComparison) {
			return searchOption.label;
		}

		return <DataAccessCompareOption option={ searchOption } />;
	}, [ showCompare ]);

	return (
		<>
			<Space.Compact
				className='data-access-search'
				size='large'>
				<Select
					allowClear={ allowClear }
					className='data-access-select'
					defaultValue={ defaultValue }
					filterOption={ false }
					labelInValue
					listHeight={ 500 }
					maxLength={ 128 }
					notFoundContent={ getNotFoundContent }
					onSearch={ handleOnSearch }
					onSelect={ (_, option) => handleSelect(option) }
					optionLabelProp={ 'label' }
					optionRender={ renderOption }
					options={ options }
					placeholder={ placeholder ?? COPY_SPECIFIC.SEARCH_FOR_DATA_ACCESS }
					showSearch
					suffixIcon={ null } />
				<Button
					icon={ <SearchOutlined /> }
					type='primary' />
			</Space.Compact>
			{ showRecent && <RecentSearchResults onSelect={ handleSelect } /> }
		</>
	);
};
